import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import PaidReceiptsImportModal from '../../components/PaidReceiptsImportModal';
import YearPicker from '../../components/YearPicker';
import AuthService from '../../services/authService';
import { listRecoveryDues } from '../../services/recoveryDuesService';
import {
  bulkAssignCounselor,
  bulkUpsertBranchNewIncome,
  deleteBranchNewIncomeById,
  listBranchIncomeCounselors,
  listBranchNewIncome,
  previewAssignCounselor,
  previewBranchNewIncomeImport,
} from '../../services/branchNewIncomeService';

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const MONTHS = [
  { value: 'all', label: 'All Months' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currencyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatAmount(value) {
  const amount = Number(value) || 0;
  return `Rs ${currencyFormatter.format(amount)}`;
}

function formatPercent(value) {
  const amount = Number(value) || 0;
  return `${amount.toFixed(2)}%`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatDecimal(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return String(value);
  }
  return currencyFormatter.format(numberValue);
}

function getMonthLabel(monthValue) {
  return MONTHS.find((month) => month.value === String(monthValue))?.label || 'All Months';
}

function toSafeFileName(value) {
  return String(value || '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function extractRowsFromHtmlTable(rawHtml) {
  const html = String(rawHtml || '').trim();
  if (!html) {
    throw new Error('Paste the HTML table before generating a preview.');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  const rowNodes = table
    ? Array.from(table.querySelectorAll('tbody tr'))
    : [];
  const usableRows = (rowNodes.length ? rowNodes : Array.from(doc.querySelectorAll('tr')))
    .map((row) => Array.from(row.querySelectorAll('td')).map((cell) => cell.textContent.replace(/\s+/g, ' ').trim()))
    .filter((cells) => cells.length >= 7);

  if (!usableRows.length) {
    throw new Error('No valid income rows were found in the pasted HTML table.');
  }

  return usableRows.map((cells) => ({
    receiptId: cells[0],
    transactionDate: cells[1],
    registrationId: cells[2],
    course: cells[3],
    student: cells[4],
    paymentType: cells[5],
    amount: cells[6],
  }));
}

function parseOptionalDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!match) return null;
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function parseOptionalNumber(value) {
  const raw = String(value || '').replace(/,/g, '').trim();
  if (!raw) return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Number(parsed.toFixed(2));
}

function extractAssignCounselorRowsFromHtml(rawHtml) {
  const html = String(rawHtml || '').trim();
  if (!html) {
    throw new Error('Paste the HTML table before generating a preview.');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const tables = Array.from(doc.querySelectorAll('table'));

  const pickTableByHeaders = (requiredHeaders) => {
    return tables.find((table) => {
      const headers = Array.from(table.querySelectorAll('thead th'))
        .map((th) => th.textContent.replace(/\s+/g, ' ').trim().toLowerCase());
      return requiredHeaders.every((key) => headers.some((header) => header.includes(key)));
    }) || null;
  };

  const paymentTable = pickTableByHeaders(['receipt id', 'registration id', 'payment type', 'amount']) || tables[0] || null;
  const paymentRows = (paymentTable
    ? Array.from(paymentTable.querySelectorAll('tbody tr'))
    : [])
    .map((row) => Array.from(row.querySelectorAll('td')).map((cell) => cell.textContent.replace(/\s+/g, ' ').trim()))
    .filter((cells) => cells.length >= 7)
    .map((cells) => ({
      receiptId: cells[0],
      transactionDate: cells[1],
      registrationId: cells[2],
      course: cells[3],
      student: cells[4],
      paymentType: cells[5],
      amount: cells[6],
    }));

  if (!paymentRows.length) {
    throw new Error('No valid payment detail rows were found in the pasted HTML.');
  }

  const kpiTable = pickTableByHeaders(['registration id', 'down payment completed date', 'earnable points', 'earned kpi points']);
  const kpiByRegistration = new Map();

  if (kpiTable) {
    const kpiRows = Array.from(kpiTable.querySelectorAll('tbody tr'))
      .map((row) => Array.from(row.querySelectorAll('td')).map((cell) => cell.textContent.replace(/\s+/g, ' ').trim()))
      .filter((cells) => cells.length >= 6);

    kpiRows.forEach((cells) => {
      const registrationId = cells[0] || '';
      if (!registrationId) return;
      kpiByRegistration.set(registrationId, {
        downPaymentCompletedDate: parseOptionalDate(cells[3]),
        earnablePoints: parseOptionalNumber(cells[4]),
        earnedKpiPoints: parseOptionalNumber(cells[5]),
      });
    });
  }

  return paymentRows.map((row) => {
    const kpi = kpiByRegistration.get(row.registrationId) || null;
    return {
      ...row,
      downPaymentCompletedDate: kpi?.downPaymentCompletedDate ?? null,
      earnablePoints: kpi?.earnablePoints ?? null,
      earnedKpiPoints: kpi?.earnedKpiPoints ?? null,
    };
  });
}

const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 48px;
`;

const TopDashboardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.section`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #d9e4ec;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
  overflow: hidden;
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid #e2e8f0;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: #0f172a;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.25;
`;

const HeroText = styled.p`
  margin: 0;
  max-width: none;
  color: #475569;
  font-size: 12px;
  line-height: 1.55;
`;

const HighlightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  background: #ffffff;
  color: #0f172a;
`;

const HighlightPeriodArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 18px 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const HighlightLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const HighlightValue = styled.strong`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  color: #0f172a;
`;

const HighlightStatLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const HighlightStatValue = styled.strong`
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
`;

const HighlightDualStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 14px 18px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const HighlightDualStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const HighlightSubValue = styled.span`
  font-size: 11px;
  color: #64748b;
  line-height: 1.5;
`;

const ProgressArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
`;

const ProgressTopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const ProgressPercent = styled.strong`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
`;

const ProgressMeta = styled.span`
  font-size: 11px;
  color: #475569;
`;

const ProgressBarTrack = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
`;

const ProgressSegment = styled.div`
  height: 100%;
  width: ${(p) => `${p.$width}%`};
  background: ${(p) => p.$bg};
  transition: width 0.25s ease;
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const BreakdownCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 9px 10px;
  border-radius: 10px;
  background: ${(p) => p.$bg || '#f8fafc'};
  border: 1px solid ${(p) => p.$border || '#e2e8f0'};
`;

const BreakdownLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #475569;
`;

const BreakdownValue = styled.strong`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
`;

const BreakdownHint = styled.span`
  font-size: 10px;
  color: #64748b;
`;

const HighlightActionsArea = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 12px 18px 16px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const AnalysisActionButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #0369a1 0%, #0284c7 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 16px rgba(3, 105, 161, 0.22);
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(3, 105, 161, 0.28);
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 8px rgba(3, 105, 161, 0.16);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContributionCard = styled.section`
  background: #ffffff;
  border: 1px solid #d9e4ec;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const ContributionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
`;

const ContributionTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ContributionToggle = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  overflow: hidden;
`;

const ContributionToggleButton = styled.button`
  border: none;
  background: ${(p) => (p.$active ? '#0f172a' : '#ffffff')};
  color: ${(p) => (p.$active ? '#ffffff' : '#475569')};
  font-size: 12px;
  font-weight: 700;
  padding: 7px 12px;
  cursor: pointer;
`;

const ContributionSummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContributionSummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  background: ${(p) => p.$bg || '#f1f5f9'};
  color: ${(p) => p.$color || '#334155'};
  font-size: 12px;
  font-weight: 700;
`;

const ContributionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  padding: 12px;
`;

const ContributionMiniCard = styled.div`
  border: 1px solid #dbe5ee;
  border-radius: 12px;
  background: #ffffff;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContributionName = styled.strong`
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
`;

const ContributionMeta = styled.span`
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
`;

const ContributionSales = styled.strong`
  color: #166534;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.1;
`;

const ContributionTableWrap = styled.div`
  padding: 8px 12px 12px;
  overflow-x: auto;
`;

const ContributionTable = styled.table`
  width: 100%;
  min-width: 360px;
  border-collapse: collapse;

  th,
  td {
    border-bottom: 1px solid #edf2f7;
    padding: 8px 10px;
    font-size: 13px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    color: #475569;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 800;
  }

  td {
    color: #0f172a;
  }
`;

const FiltersCard = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding: 20px 22px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const RecordViewToggle = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  overflow: hidden;
`;

const RecordViewButton = styled.button`
  border: none;
  background: ${(p) => (p.$active ? '#0f172a' : '#ffffff')};
  color: ${(p) => (p.$active ? '#ffffff' : '#475569')};
  font-size: 12px;
  font-weight: 700;
  padding: 8px 14px;
  cursor: pointer;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const YearPickerWrap = styled.div`
  width: 100%;
  max-width: 220px;
`;

const MonthYearInput = styled.input`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  background: #f8fafc;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #10b981;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  max-width: 220px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  color: #0f172a;
  background: #f8fafc;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.14);
    background: #ffffff;
  }
`;

const FilterNote = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
  align-self: end;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
`;

const StatCard = styled.div`
  background: ${(p) => p.$bg || '#ffffff'};
  border: 1px solid ${(p) => p.$border || '#e2e8f0'};
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const StatLabel = styled.span`
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const StatValue = styled.strong`
  color: ${(p) => p.$color || '#0f172a'};
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const CardTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
`;

const CardSubtitle = styled.p`
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
`;

const RegisterSearchInput = styled.input`
  min-width: 260px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.14);
  }
`;

const RowActionButton = styled.button`
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #ffe4e6;
    border-color: #fca5a5;
  }
`;

const ErrorBanner = styled.div`
  margin: 0 24px 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 14px;
  font-weight: 600;
`;

const SuccessBanner = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-size: 14px;
  font-weight: 600;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 11px 16px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(5, 150, 105, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(5, 150, 105, 0.26);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 11px 16px;
  background: #ffffff;
  color: #334155;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MetaText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  overflow: hidden;
`;

const ModalCard = styled.div`
  width: min(1180px, 100%);
  height: min(98vh, 1100px);
  max-height: calc(100vh - 16px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  margin: 8px auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 22px;
  font-weight: 800;
`;

const ModalSubtitle = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const CloseButton = styled.button`
  border: none;
  background: #f8fafc;
  color: #475569;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }
`;

const ModalBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto !important;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: linear-gradient(180deg, #fbfefd 0%, #ffffff 100%);
  min-height: 0;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 130px;
  max-height: 220px;
  resize: vertical;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  padding: 16px;
  font-size: 13px;
  line-height: 1.7;
  color: #0f172a;
  background: #ffffff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.14);
  }
`;

const PreviewCard = styled.section`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const PreviewSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
  padding: 12px 20px 0;
`;

const SummaryTile = styled.div`
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryValue = styled.strong`
  font-size: 32px;
  font-weight: 800;
  color: ${(p) => p.$color || '#0f172a'};
  line-height: 1;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const StatusTone = {
  unchanged: { bg: '#ecfdf5', border: '#86efac', color: '#166534' },
  update: { bg: '#fffbeb', border: '#fcd34d', color: '#b45309' },
  add: { bg: '#eff6ff', border: '#93c5fd', color: '#1d4ed8' },
  invalid: { bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' },
};

const AssignTone = {
  update: { bg: '#eff6ff', border: '#93c5fd', color: '#1d4ed8' },
  unchanged: { bg: '#ecfdf5', border: '#86efac', color: '#166534' },
  not_found: { bg: '#fff7ed', border: '#fdba74', color: '#c2410c' },
  invalid: { bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' },
};

const PREVIEW_COMPARE_FIELDS = [
  { field: 'transactionDate', label: 'Transaction Date' },
  { field: 'registrationId', label: 'Registration ID' },
  { field: 'course', label: 'Course' },
  { field: 'student', label: 'Student' },
  { field: 'paymentType', label: 'Payment Type' },
  { field: 'amount', label: 'Amount' },
];

function formatPreviewFieldValue(field, value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (field === 'amount') {
    return formatAmount(value);
  }

  if (field === 'transactionDate') {
    return formatDateTime(value);
  }

  return String(value);
}

const StatusRow = styled.tr`
  td {
    background: ${(p) => (p.$status === 'unchanged'
      ? '#f8fffb'
      : p.$status === 'update'
        ? '#fffdf5'
        : p.$status === 'add'
          ? '#f8fbff'
          : p.$status === 'invalid'
            ? '#fff8f8'
            : '#ffffff')};
  }

  &:hover td {
    background: ${(p) => (p.$status === 'unchanged'
      ? '#effcf5'
      : p.$status === 'update'
        ? '#fff7e8'
        : p.$status === 'add'
          ? '#edf5ff'
          : p.$status === 'invalid'
            ? '#fff1f1'
            : '#f8fffc')} !important;
  }
`;

const DiffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DiffItem = styled.span`
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 24px 22px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  flex-wrap: wrap;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const PreviewTableWrap = styled(TableWrap)`
  padding: 14px 20px 26px;
  flex: 1;
  min-height: 420px;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th {
    background: #f8fafc;
  }
`;

const AssignPreviewTableWrap = styled(PreviewTableWrap)`
  min-height: 680px;
  height: clamp(640px, 74vh, 980px);
`;

const AssignSummaryRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 20px 0;
`;

const AssignSummaryChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
`;

const AssignSummaryValue = styled.span`
  font-size: 15px;
  font-weight: 800;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1840px;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 14px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: ${(p) => (p.$alignRight ? 'right' : 'left')};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 16px 18px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  font-size: 14px;
  vertical-align: top;
  text-align: ${(p) => (p.$alignRight ? 'right' : 'left')};
`;

const Tr = styled.tr`
  &:hover td {
    background: #f8fffc;
  }
`;

const ReceiptCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReceiptId = styled.strong`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const ReceiptMeta = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const PaymentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 6px 10px;
  border-radius: 999px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #047857;
  font-size: 12px;
  font-weight: 700;
`;

const AmountText = styled.strong`
  font-size: 15px;
  font-weight: 800;
  color: #166534;
`;

const EmptyState = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
`;

/* ── Counselor Detail Drawer ─────────────────────────────────────── */
const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 1100;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity 0.25s ease;
`;

const DrawerPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: min(680px, 95vw);
  background: #ffffff;
  box-shadow: -4px 0 32px rgba(15, 23, 42, 0.12);
  z-index: 1101;
  display: flex;
  flex-direction: column;
  transform: ${(p) => (p.$open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
`;

const DrawerTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const DrawerEyebrow = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #10b981;
`;

const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DrawerSubtitle = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const DrawerCloseBtn = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: #fee2e2;
    color: #dc2626;
    border-color: #fca5a5;
  }
`;

const DrawerStatsBar = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  flex-shrink: 0;
  border-bottom: 1px solid #e2e8f0;
`;

const DrawerStat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 16px;
  border-right: 1px solid #e2e8f0;
  &:last-child { border-right: none; }
`;

const DrawerStatLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const DrawerStatValue = styled.strong`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
`;

const DrawerTable = styled.table`
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;

  thead tr {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
  }

  th {
    padding: 9px 12px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
    text-align: left;
    white-space: nowrap;
  }

  th:last-child, td:last-child {
    text-align: right;
  }

  td {
    padding: 9px 12px;
    font-size: 13px;
    color: #0f172a;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: #f0fdf4;
  }
`;

const DrawerTableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-top: 2px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
  font-size: 12px;
  color: #475569;
  font-weight: 700;
`;

const CounselorClickable = styled.span`
  cursor: pointer;
  color: #0f172a;
  font-weight: 800;
  border-bottom: 1.5px dashed #10b981;
  transition: color 0.15s;
  &:hover {
    color: #059669;
    border-bottom-style: solid;
  }
`;

export default function BranchNewIncome() {
  const navigate = useNavigate();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(String(CURRENT_MONTH));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalRecords: 0,
    totalAmount: 0,
    totalPaidAmount: 0,
    dueAmount: 0,
    newIncomeAmount: 0,
    monthlyTarget: 0,
    targetMonth: CURRENT_MONTH,
    targetYear: CURRENT_YEAR,
    paymentTypeCount: 0,
  });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [htmlInput, setHtmlInput] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [updatingIncome, setUpdatingIncome] = useState(false);
  const [previewJumpKey, setPreviewJumpKey] = useState(0);
  const [htmlInputCollapsed, setHtmlInputCollapsed] = useState(false);
  const [counselorContribution, setCounselorContribution] = useState([]);
  const [contributionView, setContributionView] = useState('cards');
  const [assignCounselorModalOpen, setAssignCounselorModalOpen] = useState(false);
  const [assignCounselorInput, setAssignCounselorInput] = useState('');
  const [assignCounselorInputCollapsed, setAssignCounselorInputCollapsed] = useState(false);
  const [paidReceiptModalOpen, setPaidReceiptModalOpen] = useState(false);
  const [recordView, setRecordView] = useState('new-income');
  const [recoveryDueRecords, setRecoveryDueRecords] = useState([]);
  const [dueIncomeLoading, setDueIncomeLoading] = useState(false);
  const [dueIncomeError, setDueIncomeError] = useState('');
  const [assignCounselorOption, setAssignCounselorOption] = useState(null);
  const [counselorStaffOptions, setCounselorStaffOptions] = useState([]);
  const [counselorOptionsLoading, setCounselorOptionsLoading] = useState(false);
  const [assignCounselorPreviewLoading, setAssignCounselorPreviewLoading] = useState(false);
  const [assignCounselorPreviewError, setAssignCounselorPreviewError] = useState('');
  const [assignCounselorPreview, setAssignCounselorPreview] = useState(null);
  const [assignCounselorUpdating, setAssignCounselorUpdating] = useState(false);
  const [registerSearch, setRegisterSearch] = useState('');

  const [drawerCounselor, setDrawerCounselor] = useState(null); // counselor contribution row
  const isAllRegistrationsDrawer = drawerCounselor?.scope === 'all';
  const selectedCounselorName = drawerCounselor?.counselorName || drawerCounselor?.name || '';

  const drawerRows = useMemo(() => {
    if (!drawerCounselor) return [];
    if (isAllRegistrationsDrawer) return rows;
    return rows.filter((r) => (r.resolvedCounselorName || r.registeredCounselorName || 'Unassigned') === selectedCounselorName);
  }, [drawerCounselor, rows, selectedCounselorName, isAllRegistrationsDrawer]);

  const filteredNewIncomeRows = useMemo(() => {
    const query = registerSearch.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
      const receipt = String(row?.receiptId || '').toLowerCase();
      const registration = String(row?.registrationId || '').toLowerCase();
      return receipt.includes(query) || registration.includes(query);
    });
  }, [rows, registerSearch]);

  useEffect(() => {
    let ignore = false;

    async function loadData() {
      try {
        setLoading(true);
        setError('');
        const data = await listBranchNewIncome({ year, month });
        if (!ignore) {
          setRows(Array.isArray(data?.data) ? data.data : []);
          setCounselorContribution(Array.isArray(data?.counselorContribution) ? data.counselorContribution : []);
          setSummary({
            totalRecords: Number(data?.summary?.totalRecords) || 0,
            totalAmount: Number(data?.summary?.totalAmount) || 0,
            totalPaidAmount: Number(data?.summary?.totalPaidAmount) || 0,
            dueAmount: Number(data?.summary?.dueAmount) || 0,
            newIncomeAmount: Number(data?.summary?.newIncomeAmount) || 0,
            monthlyTarget: Number(data?.summary?.monthlyTarget) || 0,
            targetMonth: Number(data?.summary?.targetMonth) || CURRENT_MONTH,
            targetYear: Number(data?.summary?.targetYear) || year,
            paymentTypeCount: Number(data?.summary?.paymentTypeCount) || 0,
          });
          const nextAvailableMonths = Array.isArray(data?.availableMonths) ? data.availableMonths : [];
          setAvailableMonths(nextAvailableMonths);
        }
      } catch (err) {
        if (!ignore) {
          setRows([]);
          setCounselorContribution([]);
          setSummary({
            totalRecords: 0,
            totalAmount: 0,
            totalPaidAmount: 0,
            dueAmount: 0,
            newIncomeAmount: 0,
            monthlyTarget: 0,
            targetMonth: CURRENT_MONTH,
            targetYear: year,
            paymentTypeCount: 0,
          });
          setAvailableMonths([]);
          setError(err?.response?.data?.error || 'Failed to load branch new income records.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      ignore = true;
    };
  }, [year, month, refreshKey]);

  const selectedRecoveryMonth = month === 'all'
    ? ''
    : `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => {
    let ignore = false;

    async function loadDueIncomeRecords() {
      if (!selectedRecoveryMonth) {
        setRecoveryDueRecords([]);
        setDueIncomeError('');
        setDueIncomeLoading(false);
        return;
      }

      try {
        setDueIncomeLoading(true);
        setDueIncomeError('');
        const data = await listRecoveryDues({ month: selectedRecoveryMonth });
        if (!ignore) {
          setRecoveryDueRecords(Array.isArray(data?.dues) ? data.dues : []);
        }
      } catch (err) {
        if (!ignore) {
          setRecoveryDueRecords([]);
          setDueIncomeError(err?.response?.data?.message || 'Failed to load due income records.');
        }
      } finally {
        if (!ignore) {
          setDueIncomeLoading(false);
        }
      }
    }

    loadDueIncomeRecords();
    return () => {
      ignore = true;
    };
  }, [selectedRecoveryMonth, refreshKey]);

  useEffect(() => {
    if (!previewData || previewJumpKey === 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      const previewSection = document.getElementById('branch-new-income-import-preview');
      if (previewSection) {
        previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [previewData, previewJumpKey]);

  const monthOptions = useMemo(() => {
    if (!availableMonths.length) {
      return MONTHS;
    }
    const allowed = new Set(availableMonths.map((value) => String(value)));
    return MONTHS.filter((entry) => entry.value === 'all' || allowed.has(entry.value));
  }, [availableMonths]);

  const selectedPeriodLabel = month === 'all' ? `${year}` : `${getMonthLabel(month)} ${year}`;
  const previewSummary = previewData?.summary || { add: 0, update: 0, unchanged: 0, invalid: 0, total: 0 };
  const canApplyUpdate = Boolean(previewData) && previewSummary.invalid === 0 && (previewSummary.add > 0 || previewSummary.update > 0);
  const assignCounselorSummary = assignCounselorPreview?.summary || { total: 0, update: 0, unchanged: 0, notFound: 0, invalid: 0 };
  const canApplyCounselorAssign = Boolean(assignCounselorPreview)
    && assignCounselorSummary.invalid === 0
    && assignCounselorSummary.update > 0;
  const contributionTotalRegs = counselorContribution.reduce((sum, row) => sum + (Number(row.registrations) || 0), 0);
  const contributionTotalSales = counselorContribution.reduce((sum, row) => sum + (Number(row.salesAmount) || 0), 0);
  const paidDueIncomeRows = useMemo(
    () => recoveryDueRecords.filter((row) => String(row?.dueStatus || '').toLowerCase() === 'paid'),
    [recoveryDueRecords]
  );
  const dueIncomeTotalAmount = useMemo(
    () => paidDueIncomeRows.reduce((sum, row) => sum + (Number(row?.confirmedAmount) || 0), 0),
    [paidDueIncomeRows]
  );
  const dueIncomeStatusCount = useMemo(
    () => new Set(paidDueIncomeRows.map((row) => String(row?.dueStatus || '').trim()).filter(Boolean)).size,
    [paidDueIncomeRows]
  );
  const overviewTarget = Number(summary.monthlyTarget) || 0;
  const overviewPaid = dueIncomeTotalAmount;
  const overviewDue = dueIncomeTotalAmount;
  const overviewNewIncome = Number(summary.totalAmount) || 0;
  const overviewBreakdownTotal = overviewNewIncome + overviewDue;
  const overviewRemaining = Math.max(overviewTarget - overviewBreakdownTotal, 0);
  const overviewAchievedPercent = overviewTarget > 0 ? (overviewBreakdownTotal / overviewTarget) * 100 : 0;
  const overviewAchievedPercentClamped = Math.min(Math.max(overviewAchievedPercent, 0), 100);
  const overviewNewIncomeShare = overviewBreakdownTotal > 0 ? (overviewNewIncome / overviewBreakdownTotal) * 100 : 0;
  const overviewDueShare = overviewBreakdownTotal > 0 ? (overviewDue / overviewBreakdownTotal) * 100 : 0;
  const overviewNewIncomeBarPercent = (overviewAchievedPercentClamped * overviewNewIncomeShare) / 100;
  const overviewDueBarPercent = (overviewAchievedPercentClamped * overviewDueShare) / 100;
  const targetPeriodLabel = `${getMonthLabel(summary.targetMonth || CURRENT_MONTH)} ${summary.targetYear || year}`;
  const topSummary = recordView === 'new-income'
    ? {
        countLabel: 'Total Receipts',
        countValue: summary.totalRecords,
        amountLabel: 'Total Income',
        amountValue: formatAmount(summary.totalAmount),
        metaLabel: 'Payment Types',
        metaValue: summary.paymentTypeCount,
      }
    : {
        countLabel: 'Paid Students',
        countValue: paidDueIncomeRows.length,
        amountLabel: 'Due Income',
        amountValue: formatAmount(dueIncomeTotalAmount),
        metaLabel: 'Due Statuses',
        metaValue: dueIncomeStatusCount,
      };

  const canManageIncomeActions = useMemo(() => {
    const user = AuthService.getUser();
    const isSuperAdmin = Boolean(user?.superAdmin?.superAdminId);
    const role = String(
      user?.superAdmin?.staff?.role ||
      user?.superAdmin?.role ||
      user?.superAdmin?.staff?.staffType ||
      user?.superAdmin?.staff?.type ||
      ''
    ).trim().toLowerCase();
    return isSuperAdmin || role === 'admin' || role === 'administrator';
  }, []);

  useEffect(() => {
    if (!assignCounselorModalOpen) {
      return;
    }

    let ignore = false;

    async function loadCounselorOptions() {
      try {
        setCounselorOptionsLoading(true);
        const data = await listBranchIncomeCounselors();
        if (ignore) {
          return;
        }

        const options = (Array.isArray(data?.data) ? data.data : [])
          .filter((row) => row?.staffId && row?.name)
          .map((row) => {
            const meta = [row.positionName, row.employeeNumber ? `EMP ${row.employeeNumber}` : null, row.type]
              .filter(Boolean)
              .join(' • ');

            return {
              value: row.staffId,
              label: `${row.name} (ID ${row.staffId})`,
              data: row,
              meta,
            };
          });

        setCounselorStaffOptions(options);
      } catch (err) {
        if (!ignore) {
          setAssignCounselorPreviewError(err?.response?.data?.error || 'Failed to load staff details.');
        }
      } finally {
        if (!ignore) {
          setCounselorOptionsLoading(false);
        }
      }
    }

    loadCounselorOptions();

    return () => {
      ignore = true;
    };
  }, [assignCounselorModalOpen]);

  const openImportModal = () => {
    setImportModalOpen(true);
    setPreviewError('');
    setHtmlInputCollapsed(false);
  };

  const closeImportModal = () => {
    if (previewLoading || updatingIncome) {
      return;
    }
    setImportModalOpen(false);
    setHtmlInput('');
    setPreviewData(null);
    setPreviewError('');
    setHtmlInputCollapsed(false);
  };

  const handleGeneratePreview = async () => {
    try {
      setPreviewLoading(true);
      setPreviewError('');
      const parsedRows = extractRowsFromHtmlTable(htmlInput);
      const data = await previewBranchNewIncomeImport({ rows: parsedRows });
      setPreviewData(data);
      setPreviewJumpKey((value) => value + 1);
      setHtmlInputCollapsed(true);
    } catch (err) {
      setPreviewData(null);
      setPreviewError(err?.response?.data?.error || err.message || 'Failed to generate import preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApplyUpdate = async () => {
    if (!previewData) {
      return;
    }

    try {
      setUpdatingIncome(true);
      setPreviewError('');
      const response = await bulkUpsertBranchNewIncome({ rows: previewData.previewRows || [] });
      setSuccessMessage(response?.message || 'Branch new income was updated successfully.');
      setImportModalOpen(false);
      setHtmlInput('');
      setPreviewData(null);
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setPreviewError(err?.response?.data?.error || 'Failed to update branch new income.');
      if (err?.response?.data?.preview) {
        setPreviewData(err.response.data.preview);
      }
    } finally {
      setUpdatingIncome(false);
    }
  };

  const handlePaidReceiptImportCompleted = async (result) => {
    setSuccessMessage(
      `${result?.updated || 0} updated, ${result?.inserted || 0} inserted, ${result?.skipped || 0} skipped, ${result?.errors || 0} errors.`
    );
    setPaidReceiptModalOpen(false);
    setRefreshKey((value) => value + 1);
  };

  const openAssignCounselorModal = () => {
    setAssignCounselorModalOpen(true);
    setAssignCounselorPreviewError('');
    setAssignCounselorInputCollapsed(false);
  };

  const closeAssignCounselorModal = () => {
    if (assignCounselorPreviewLoading || assignCounselorUpdating) {
      return;
    }
    setAssignCounselorModalOpen(false);
    setAssignCounselorInput('');
    setAssignCounselorInputCollapsed(false);
    setAssignCounselorOption(null);
    setAssignCounselorPreview(null);
    setAssignCounselorPreviewError('');
  };

  const handlePreviewCounselorAssign = async () => {
    try {
      setAssignCounselorPreviewLoading(true);
      setAssignCounselorPreviewError('');
      const parsedRows = extractAssignCounselorRowsFromHtml(assignCounselorInput);
      const data = await previewAssignCounselor({
        counselorName: assignCounselorOption ? String(assignCounselorOption.value) : '',
        rows: parsedRows,
      });
      setAssignCounselorPreview(data);
      setAssignCounselorInputCollapsed(true);
    } catch (err) {
      setAssignCounselorPreview(null);
      setAssignCounselorPreviewError(err?.response?.data?.error || err.message || 'Failed to preview counselor assignment.');
    } finally {
      setAssignCounselorPreviewLoading(false);
    }
  };

  const handleApplyCounselorAssign = async () => {
    if (!assignCounselorPreview) {
      return;
    }

    try {
      setAssignCounselorUpdating(true);
      setAssignCounselorPreviewError('');
      const response = await bulkAssignCounselor({
        counselorName: assignCounselorOption ? String(assignCounselorOption.value) : '',
        rows: assignCounselorPreview.previewRows || [],
      });
      setSuccessMessage(response?.message || 'Counselor assignment completed successfully.');
      setAssignCounselorModalOpen(false);
      setAssignCounselorInput('');
      setAssignCounselorOption(null);
      setAssignCounselorPreview(null);
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setAssignCounselorPreviewError(err?.response?.data?.error || 'Failed to assign counselor.');
      if (err?.response?.data?.previewRows) {
        setAssignCounselorPreview(err.response.data);
      }
    } finally {
      setAssignCounselorUpdating(false);
    }
  };

  const handleViewAnalysis = () => {
    const selectedMonth = month === 'all' ? CURRENT_MONTH : Number(month);
    const analysisUrl = `/admin-sales/branch-targets/new-income-analysis?year=${year}&month=${selectedMonth}`;
    window.open(analysisUrl, '_blank');
  };

  const handleDeleteNewIncomeRow = async (row) => {
    if (!row?.id) {
      return;
    }

    const result = await Swal.fire({
      title: 'Delete this income record?',
      text: `Receipt ${row.receiptId || `#${row.id}`} will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await deleteBranchNewIncomeById(row.id);
      setSuccessMessage(response?.message || 'Income entry deleted successfully.');
      setRefreshKey((value) => value + 1);
    } catch (err) {
      await Swal.fire({
        title: 'Unable to delete',
        text: err?.response?.data?.error || 'Failed to delete branch new income record.',
        icon: 'error',
        confirmButtonColor: '#b91c1c',
      });
    }
  };

  const handleExportCounselorExcel = () => {
    if (!drawerCounselor || drawerRows.length === 0) {
      return;
    }

    const exportRows = drawerRows.map((row, index) => ({
      'No': index + 1,
      'Entry ID': row.id,
      'Receipt ID': row.receiptId || '',
      'Transaction Date': formatDateTime(row.transactionDate),
      'Registration ID': row.registrationId || '',
      'Student': row.student || '',
      'Course': row.course || '',
      'Resolved Counselor': row.resolvedCounselorName || row.registeredCounselorName || 'Unassigned',
      'Registered Counselor': row.registeredCounselorName || 'Unassigned',
      'Down Payment Completed Date': formatDateOnly(row.downPaymentCompletedDate),
      'Payment Type': row.paymentType || '',
      'Amount': Number(row.amount) || 0,
      'Earnable Points': row.earnablePoints === null || row.earnablePoints === undefined ? '' : Number(row.earnablePoints),
      'Earned KPI Points': row.earnedKpiPoints === null || row.earnedKpiPoints === undefined ? '' : Number(row.earnedKpiPoints),
      'Created At': formatDateTime(row.createdAt),
      'Updated At': formatDateTime(row.updatedAt),
    }));

    const sheet = XLSX.utils.json_to_sheet(exportRows);
    sheet['!cols'] = [
      { wch: 6 },
      { wch: 10 },
      { wch: 16 },
      { wch: 22 },
      { wch: 16 },
      { wch: 24 },
      { wch: 24 },
      { wch: 22 },
      { wch: 22 },
      { wch: 18 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 22 },
      { wch: 22 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Registrations');

    const periodToken = month === 'all' ? String(year) : `${year}-${String(month).padStart(2, '0')}`;
    const counselorToken = toSafeFileName(selectedCounselorName) || 'Counselor';
    const fileName = `new-income-${periodToken}-${counselorToken}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    setSuccessMessage(`Excel exported: ${fileName}`);
  };

  return (
    <AdminSalesLayout title="New Income" currentSection="branchNewIncome">
      <PageSection>
        {successMessage && <SuccessBanner>{successMessage}</SuccessBanner>}

        <TopDashboardGrid>
          <HeroCard>
            <HeroContent>
            </HeroContent>
            <HighlightPanel>
              <HighlightPeriodArea>
                <HighlightLabel>Selected Period</HighlightLabel>
                <MonthYearInput
                  type="month"
                  value={month !== 'all' ? `${year}-${String(month).padStart(2, '0')}` : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const [y, m] = v.split('-');
                    setYear(Number(y));
                    setMonth(String(Number(m)));
                  }}
                />
              </HighlightPeriodArea>
              <HighlightDualStatGrid>
                <HighlightDualStatItem>
                  <HighlightStatLabel>Monthly Target</HighlightStatLabel>
                  <HighlightStatValue>{loading ? '—' : formatAmount(overviewTarget)}</HighlightStatValue>
                  <HighlightSubValue>{targetPeriodLabel}</HighlightSubValue>
                </HighlightDualStatItem>
                <HighlightDualStatItem>
                  <HighlightStatLabel>Total Paid (Incl. Extra)</HighlightStatLabel>
                  <HighlightStatValue>{loading ? '—' : formatAmount(overviewPaid)}</HighlightStatValue>
                  <HighlightSubValue>{loading ? '—' : `${summary.totalRecords} receipt${summary.totalRecords === 1 ? '' : 's'}`}</HighlightSubValue>
                </HighlightDualStatItem>
              </HighlightDualStatGrid>
              <ProgressArea>
                <ProgressTopRow>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <HighlightStatLabel>Achieved</HighlightStatLabel>
                    <ProgressPercent>{loading ? '—' : formatPercent(overviewAchievedPercent)}</ProgressPercent>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <HighlightStatLabel>Remaining</HighlightStatLabel>
                    <ProgressMeta>{loading ? '—' : formatAmount(overviewRemaining)}</ProgressMeta>
                  </div>
                </ProgressTopRow>
                <ProgressBarTrack aria-hidden="true">
                  <ProgressSegment $width={loading ? 0 : overviewNewIncomeBarPercent} $bg="#2563eb" />
                  <ProgressSegment $width={loading ? 0 : overviewDueBarPercent} $bg="#22c55e" />
                </ProgressBarTrack>
                <BreakdownGrid>
                  <BreakdownCard $bg="#eff6ff" $border="#bfdbfe">
                    <BreakdownLabel>New Income</BreakdownLabel>
                    <BreakdownValue>{loading ? '—' : formatAmount(overviewNewIncome)}</BreakdownValue>
                    <BreakdownHint>{loading ? '—' : `${formatPercent(overviewNewIncomeShare)} of achieved`}</BreakdownHint>
                  </BreakdownCard>
                  <BreakdownCard $bg="#ecfdf5" $border="#bbf7d0">
                    <BreakdownLabel>Due Payments</BreakdownLabel>
                    <BreakdownValue>{loading ? '—' : formatAmount(overviewDue)}</BreakdownValue>
                    <BreakdownHint>{loading ? '—' : `${formatPercent(overviewDueShare)} of achieved`}</BreakdownHint>
                  </BreakdownCard>
                </BreakdownGrid>
              </ProgressArea>
              <HighlightActionsArea>
                <AnalysisActionButton type="button" onClick={handleViewAnalysis}>
                  📊 Analysis
                </AnalysisActionButton>
              </HighlightActionsArea>
            </HighlightPanel>
          </HeroCard>

          <ContributionCard>
          <ContributionHeader>
            <ContributionTitleWrap>
              <CardTitle style={{ fontSize: 16, margin: 0 }}>Counselor Contribution</CardTitle>
              <CardSubtitle style={{ margin: 0 }}>
                Two view modes for {selectedPeriodLabel}: quick cards and detailed table.
              </CardSubtitle>
            </ContributionTitleWrap>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <ContributionSummaryRow>
                <ContributionSummaryBadge $bg="#ecfdf5" $color="#166534">
                  Sales {formatAmount(contributionTotalSales)}
                </ContributionSummaryBadge>
                <ContributionSummaryBadge $bg="#eff6ff" $color="#1d4ed8">
                  Regs {contributionTotalRegs}
                </ContributionSummaryBadge>
                <CounselorClickable
                  onClick={() => setDrawerCounselor({
                    scope: 'all',
                    counselorName: 'All Counselors',
                    registrations: rows.length,
                    salesAmount: rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
                    kpiAchieved: null,
                    kpiLocked: null,
                  })}
                >
                  View All Registrations
                </CounselorClickable>
              </ContributionSummaryRow>

              <ContributionToggle>
                <ContributionToggleButton
                  type="button"
                  $active={contributionView === 'cards'}
                  onClick={() => setContributionView('cards')}
                >
                  Cards
                </ContributionToggleButton>
                <ContributionToggleButton
                  type="button"
                  $active={contributionView === 'table'}
                  onClick={() => setContributionView('table')}
                >
                  Table
                </ContributionToggleButton>
              </ContributionToggle>
            </div>
          </ContributionHeader>

          {counselorContribution.length === 0 ? (
            <EmptyState style={{ padding: '24px 16px' }}>
              No counselor contribution data available for this period.
            </EmptyState>
          ) : contributionView === 'cards' ? (
            <ContributionGrid>
              {counselorContribution.map((item) => {
                const regs = Number(item.registrations) || 0;
                const sales = Number(item.salesAmount) || 0;
                const share = contributionTotalSales > 0 ? ((sales / contributionTotalSales) * 100) : 0;
                const achieved = Number(item.kpiAchieved) || 0;
                const locked = item.kpiLocked !== null && item.kpiLocked !== undefined ? Number(item.kpiLocked) : null;
                const achievedPct = locked > 0 ? (achieved / locked) * 100 : null;
                return (
                  <ContributionMiniCard key={item.counselorName}>
                    <ContributionName>
                      <CounselorClickable onClick={() => setDrawerCounselor(item)}>{item.counselorName}</CounselorClickable>
                    </ContributionName>
                    <ContributionMeta>{regs} registration{regs === 1 ? '' : 's'}</ContributionMeta>
                    <ContributionSales>{formatAmount(sales)}</ContributionSales>
                    <ContributionMeta>{share.toFixed(1)}% sales share</ContributionMeta>
                    {locked !== null && (
                      <ContributionMeta style={{ marginTop: 4 }}>
                        KPI {achieved.toFixed(1)}{achievedPct !== null ? ` (${achievedPct.toFixed(1)}%)` : ''} / {locked.toFixed(1)} locked
                      </ContributionMeta>
                    )}
                  </ContributionMiniCard>
                );
              })}
            </ContributionGrid>
          ) : (
            <ContributionTableWrap>
              <ContributionTable>
                <thead>
                  <tr>
                    <th>Counselor</th>
                    <th>Regs</th>
                    <th>Sales</th>
                    <th>KPI Achieved</th>
                    <th>KPI Locked</th>
                  </tr>
                </thead>
                <tbody>
                  {counselorContribution.map((item) => {
                    const achieved = Number(item.kpiAchieved) || 0;
                    const locked = item.kpiLocked !== null && item.kpiLocked !== undefined ? Number(item.kpiLocked) : null;
                    const achievedPct = locked > 0 ? (achieved / locked) * 100 : null;
                    return (
                      <tr key={item.counselorName}>
                        <td><CounselorClickable onClick={() => setDrawerCounselor(item)}>{item.counselorName}</CounselorClickable></td>
                        <td>{Number(item.registrations) || 0}</td>
                        <td>{formatAmount(item.salesAmount)}</td>
                        <td>
                          {achieved.toFixed(1)}
                          {achievedPct !== null && (
                            <span style={{ color: '#64748b', fontSize: '11px', marginLeft: 4 }}>({achievedPct.toFixed(1)}%)</span>
                          )}
                        </td>
                        <td>{locked !== null ? locked.toFixed(1) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </ContributionTable>
            </ContributionTableWrap>
          )}
          {canManageIncomeActions && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '12px 16px 16px' }}>
              <PrimaryButton type="button" onClick={openImportModal}>
                Update New Income
              </PrimaryButton>
              <SecondaryButton type="button" onClick={openAssignCounselorModal}>
                Assign Counselor
              </SecondaryButton>
              <SecondaryButton type="button" onClick={() => setPaidReceiptModalOpen(true)}>
                Import Paid Receipts
              </SecondaryButton>
            </div>
          )}
          </ContributionCard>
        </TopDashboardGrid>

        <StatsRow>
          <StatCard>
            <StatLabel>{topSummary.countLabel}</StatLabel>
            <StatValue>{recordView === 'new-income' && loading ? '—' : recordView === 'due-income' && dueIncomeLoading ? '—' : topSummary.countValue}</StatValue>
          </StatCard>
          <StatCard $bg="#f0fdf4" $border="#bbf7d0">
            <StatLabel>{topSummary.amountLabel}</StatLabel>
            <StatValue $color="#166534">{recordView === 'new-income' && loading ? '—' : recordView === 'due-income' && dueIncomeLoading ? '—' : topSummary.amountValue}</StatValue>
          </StatCard>
          <StatCard $bg="#eff6ff" $border="#bfdbfe">
            <StatLabel>{topSummary.metaLabel}</StatLabel>
            <StatValue $color="#1d4ed8">{recordView === 'new-income' && loading ? '—' : recordView === 'due-income' && dueIncomeLoading ? '—' : topSummary.metaValue}</StatValue>
          </StatCard>
          <StatCard $bg="#fff7ed" $border="#fed7aa">
            <StatLabel>Active Filter</StatLabel>
            <StatValue $color="#c2410c">{month === 'all' ? 'Yearly' : 'Monthly'}</StatValue>
          </StatCard>
        </StatsRow>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>{recordView === 'new-income' ? 'Branch New Income Register' : 'Due Income Register'}</CardTitle>
              <CardSubtitle>
                {recordView === 'new-income'
                  ? `Receipt-level income details for ${selectedPeriodLabel}.`
                  : `Paid student details from Recovery Dues for ${selectedPeriodLabel}.`}
              </CardSubtitle>
            </div>
            <CardActions>
              {recordView === 'new-income' && (
                <RegisterSearchInput
                  type="text"
                  value={registerSearch}
                  onChange={(e) => setRegisterSearch(e.target.value)}
                  placeholder="Search by Receipt or Registration ID"
                  aria-label="Search new income register"
                />
              )}
              <RecordViewToggle>
                <RecordViewButton
                  type="button"
                  $active={recordView === 'new-income'}
                  onClick={() => setRecordView('new-income')}
                >
                  New Income
                </RecordViewButton>
                <RecordViewButton
                  type="button"
                  $active={recordView === 'due-income'}
                  onClick={() => setRecordView('due-income')}
                >
                  Due Income
                </RecordViewButton>
              </RecordViewToggle>
              <Badge>
                {recordView === 'new-income'
                  ? (loading ? 'Loading...' : `${filteredNewIncomeRows.length} Rows`)
                  : (dueIncomeLoading ? 'Loading...' : `${paidDueIncomeRows.length} Rows`)}
              </Badge>
            </CardActions>
          </CardHeader>

          {error && <ErrorBanner>{error}</ErrorBanner>}
          {recordView === 'due-income' && dueIncomeError && <ErrorBanner>{dueIncomeError}</ErrorBanner>}

          <TableWrap>
            <Table>
              {recordView === 'new-income' ? (
                <>
                  <thead>
                    <tr>
                      <Th>Entry ID</Th>
                      <Th>Receipt</Th>
                      <Th>Transaction Date</Th>
                      <Th>Registration ID</Th>
                      <Th>Course</Th>
                      <Th>Student</Th>
                      <Th>Registered Counselor</Th>
                      <Th>Down Payment Completed</Th>
                      <Th $alignRight>Earnable Points</Th>
                      <Th $alignRight>Earned KPI Points</Th>
                      <Th>Payment Type</Th>
                      <Th $alignRight>Amount</Th>
                      <Th>Created At</Th>
                      <Th>Updated At</Th>
                      {canManageIncomeActions && <Th>Action</Th>}
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && filteredNewIncomeRows.length === 0 ? (
                      <tr>
                        <Td colSpan={canManageIncomeActions ? 15 : 14}>
                          <EmptyState>
                            {rows.length === 0
                              ? 'No branch new income records were found for the selected period.'
                              : 'No records matched your search. Try Receipt ID or Registration ID.'}
                          </EmptyState>
                        </Td>
                      </tr>
                    ) : (
                      filteredNewIncomeRows.map((row) => (
                        <Tr key={row.id}>
                          <Td>{row.id}</Td>
                          <Td>
                            <ReceiptCell>
                              <ReceiptId>{row.receiptId}</ReceiptId>
                              <ReceiptMeta>Income entry #{row.id}</ReceiptMeta>
                            </ReceiptCell>
                          </Td>
                          <Td>{formatDateTime(row.transactionDate)}</Td>
                          <Td>{row.registrationId}</Td>
                          <Td>{row.course}</Td>
                          <Td>{row.student}</Td>
                          <Td>
                            {row.registeredCounselorName
                              ? `${row.registeredCounselorName}${row.registeredCounselorStaffId ? ` (ID ${row.registeredCounselorStaffId})` : ''}`
                              : 'Unassigned'}
                          </Td>
                          <Td>{formatDateOnly(row.downPaymentCompletedDate)}</Td>
                          <Td $alignRight>{formatDecimal(row.earnablePoints)}</Td>
                          <Td $alignRight>{formatDecimal(row.earnedKpiPoints)}</Td>
                          <Td>
                            <PaymentBadge>{row.paymentType}</PaymentBadge>
                          </Td>
                          <Td $alignRight>
                            <AmountText>{formatAmount(row.amount)}</AmountText>
                          </Td>
                          <Td>{formatDateTime(row.createdAt)}</Td>
                          <Td>{formatDateTime(row.updatedAt)}</Td>
                          {canManageIncomeActions && (
                            <Td>
                              <RowActionButton type="button" onClick={() => handleDeleteNewIncomeRow(row)}>
                                Delete
                              </RowActionButton>
                            </Td>
                          )}
                        </Tr>
                      ))
                    )}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <Th>Due ID</Th>
                      <Th>Student</Th>
                      <Th>Course</Th>
                      <Th>Batch</Th>
                      <Th>Report Month</Th>
                      <Th>Due Status</Th>
                      <Th $alignRight>Due Amount</Th>
                      <Th $alignRight>Confirmed Amount</Th>
                      <Th>Confirm Date</Th>
                      <Th>Updated At</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedRecoveryMonth ? (
                      <tr>
                        <Td colSpan={10}>
                          <EmptyState>
                            Select a month to load due income records and enable accurate paid receipt matching.
                          </EmptyState>
                        </Td>
                      </tr>
                    ) : (!dueIncomeLoading && paidDueIncomeRows.length === 0) ? (
                      <tr>
                        <Td colSpan={10}>
                          <EmptyState>
                            No paid due records were found for the selected month.
                          </EmptyState>
                        </Td>
                      </tr>
                    ) : (
                      paidDueIncomeRows.map((row) => (
                        <Tr key={row.dueId}>
                          <Td>{row.dueId}</Td>
                          <Td>
                            <ReceiptCell>
                              <ReceiptId>{row.studentName || '—'}</ReceiptId>
                              <ReceiptMeta>{row.studentId || '—'}</ReceiptMeta>
                            </ReceiptCell>
                          </Td>
                          <Td>{row.course || '—'}</Td>
                          <Td>{row.batchName || '—'}</Td>
                          <Td>{row.reportMonth || '—'}</Td>
                          <Td>
                            <PaymentBadge>{row.dueStatus || '—'}</PaymentBadge>
                          </Td>
                          <Td $alignRight>
                            <AmountText>{formatAmount(row.dueAmount)}</AmountText>
                          </Td>
                          <Td $alignRight>
                            <AmountText>{formatAmount(row.confirmedAmount)}</AmountText>
                          </Td>
                          <Td>{formatDateOnly(row.confirmDateToPay)}</Td>
                          <Td>{formatDateTime(row.updatedDate)}</Td>
                        </Tr>
                      ))
                    )}
                  </tbody>
                </>
              )}
            </Table>
          </TableWrap>
        </Card>

        {importModalOpen && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitleWrap>
                  <ModalTitle>Update Branch New Income</ModalTitle>
                  <ModalSubtitle>
                    Paste source receipt HTML, preview the row-by-row comparison, then apply the bulk update.
                  </ModalSubtitle>
                </ModalTitleWrap>
                <CloseButton type="button" onClick={closeImportModal} aria-label="Close import modal">
                  ×
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                <div>
                  <FieldLabel htmlFor="branch-new-income-html">HTML Table</FieldLabel>
                  {htmlInputCollapsed && previewData ? (
                    <div style={{ border: '1px solid #dbeafe', background: '#f8fbff', borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <MetaText style={{ margin: 0, color: '#1e3a8a', fontWeight: 600 }}>
                        HTML table is collapsed to maximize preview space ({htmlInput.length.toLocaleString()} chars)
                      </MetaText>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <SecondaryButton
                          type="button"
                          onClick={() => setHtmlInputCollapsed(false)}
                          style={{ padding: '8px 12px', borderRadius: 10, fontSize: 12 }}
                        >
                          Edit HTML
                        </SecondaryButton>
                        <SecondaryButton
                          type="button"
                          onClick={() => {
                            setHtmlInput('');
                            setPreviewData(null);
                            setPreviewError('');
                            setHtmlInputCollapsed(false);
                          }}
                          style={{ padding: '8px 12px', borderRadius: 10, fontSize: 12 }}
                        >
                          Clear
                        </SecondaryButton>
                      </div>
                    </div>
                  ) : (
                    <TextArea
                      id="branch-new-income-html"
                      value={htmlInput}
                      onChange={(event) => {
                        setHtmlInput(event.target.value);
                        setPreviewError('');
                      }}
                      placeholder="Paste the full HTML table here..."
                    />
                  )}
                </div>

                {previewError && <ErrorBanner style={{ margin: 0 }}>{previewError}</ErrorBanner>}

                {previewData && (
                  <PreviewCard id="branch-new-income-import-preview">
                    <PreviewHeader>
                      <div>
                        <CardTitle>Import Preview</CardTitle>
                        <CardSubtitle>
                          Review receipt status before updating branch new income.
                        </CardSubtitle>
                      </div>
                      <StatusBadge
                        $bg="#eff6ff"
                        $border="#bfdbfe"
                        $color="#1d4ed8"
                      >
                        {previewSummary.total} Parsed Rows
                      </StatusBadge>
                    </PreviewHeader>

                    <PreviewSummaryGrid>
                      <SummaryTile $bg="#eff6ff" $border="#bfdbfe">
                        <StatLabel>Total Rows</StatLabel>
                        <SummaryValue $color="#1d4ed8">{previewSummary.total}</SummaryValue>
                      </SummaryTile>
                      <SummaryTile $bg="#ecfdf5" $border="#86efac">
                        <StatLabel>Matches Existing</StatLabel>
                        <SummaryValue $color="#166534">{previewSummary.unchanged}</SummaryValue>
                      </SummaryTile>
                      <SummaryTile $bg="#fffbeb" $border="#fcd34d">
                        <StatLabel>Need Update</StatLabel>
                        <SummaryValue $color="#b45309">{previewSummary.update}</SummaryValue>
                      </SummaryTile>
                      <SummaryTile $bg="#f8fbff" $border="#93c5fd">
                        <StatLabel>Plan To Add</StatLabel>
                        <SummaryValue $color="#1d4ed8">{previewSummary.add}</SummaryValue>
                      </SummaryTile>
                      <SummaryTile $bg="#fef2f2" $border="#fca5a5">
                        <StatLabel>Needs Attention</StatLabel>
                        <SummaryValue $color="#b91c1c">{previewSummary.invalid}</SummaryValue>
                      </SummaryTile>
                    </PreviewSummaryGrid>

                    <PreviewTableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <Th>#</Th>
                            <Th>Receipt</Th>
                            <Th>Action</Th>
                            <Th>In Database</Th>
                            <Th>From Import</Th>
                            <Th $alignRight>Amount</Th>
                            <Th>Validation</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.previewRows.map((row) => {
                            const tone = StatusTone[row.status] || StatusTone.add;
                            const hasErrors = row.errors?.length > 0;
                            const hasChanges = row.changedFields?.length > 0;
                            return (
                              <StatusRow key={`${row.receiptId}-${row.rowIndex}`} $status={row.status}>
                                <Td>
                                  <ReceiptMeta>#{row.rowIndex}</ReceiptMeta>
                                </Td>
                                <Td>
                                  <ReceiptCell>
                                    <ReceiptId>{row.receiptId || '—'}</ReceiptId>
                                    <ReceiptMeta>{formatDateTime(row.transactionDate)}</ReceiptMeta>
                                  </ReceiptCell>
                                </Td>
                                <Td>
                                  <StatusBadge $bg={tone.bg} $border={tone.border} $color={tone.color}>
                                    {row.statusLabel}
                                  </StatusBadge>
                                  {!hasErrors && row.status === 'add' && (
                                    <DiffList style={{ marginTop: 8 }}>
                                      <DiffItem style={{ color: '#1d4ed8' }}>New receipt will be inserted.</DiffItem>
                                    </DiffList>
                                  )}
                                  {!hasErrors && row.status === 'unchanged' && (
                                    <DiffList style={{ marginTop: 8 }}>
                                      <DiffItem style={{ color: '#166534' }}>Already matched with stored values.</DiffItem>
                                    </DiffList>
                                  )}
                                </Td>
                                <Td>
                                  {row.existing ? (
                                    <DiffList>
                                      {PREVIEW_COMPARE_FIELDS.map((item) => (
                                        <DiffItem key={item.field}>
                                          <strong>{item.label}:</strong> {formatPreviewFieldValue(item.field, row.existing[item.field])}
                                        </DiffItem>
                                      ))}
                                    </DiffList>
                                  ) : (
                                    <DiffItem style={{ color: '#1d4ed8' }}>No existing receipt in database.</DiffItem>
                                  )}
                                </Td>
                                <Td>
                                  <DiffList>
                                    {PREVIEW_COMPARE_FIELDS.map((item) => (
                                      <DiffItem key={item.field}>
                                        <strong>{item.label}:</strong> {formatPreviewFieldValue(item.field, row[item.field])}
                                      </DiffItem>
                                    ))}
                                  </DiffList>
                                </Td>
                                <Td $alignRight>
                                  <AmountText>{row.amount === null ? '—' : formatAmount(row.amount)}</AmountText>
                                </Td>
                                <Td>
                                  {hasErrors ? (
                                    <DiffList>
                                      {row.errors.map((message) => (
                                        <DiffItem key={message} style={{ color: '#b91c1c' }}>{message}</DiffItem>
                                      ))}
                                    </DiffList>
                                  ) : hasChanges ? (
                                    <DiffList>
                                      <DiffItem style={{ color: '#b45309', fontWeight: 700 }}>Changed fields:</DiffItem>
                                      {row.changedFields.map((field) => (
                                        <DiffItem key={field.field}>
                                          {field.label}: {field.field === 'amount' ? formatAmount(field.existing) : field.existing} → {field.field === 'amount' ? formatAmount(field.incoming) : field.incoming}
                                        </DiffItem>
                                      ))}
                                    </DiffList>
                                  ) : (
                                    <DiffItem style={{ color: '#166534' }}>No validation issues.</DiffItem>
                                  )}
                                </Td>
                              </StatusRow>
                            );
                          })}
                        </tbody>
                      </Table>
                    </PreviewTableWrap>
                  </PreviewCard>
                )}
              </ModalBody>

              <ModalFooter>
                <MetaText>
                  {previewData
                    ? canApplyUpdate
                      ? `Ready to apply ${previewSummary.add + previewSummary.update} database change${previewSummary.add + previewSummary.update === 1 ? '' : 's'}.`
                      : previewSummary.invalid > 0
                        ? 'Resolve the highlighted rows before updating the database.'
                        : 'No changes are required for the current preview.'
                    : 'Generate a preview to review new, changed, and existing receipts.'}
                </MetaText>
                <FooterActions>
                  <SecondaryButton type="button" onClick={closeImportModal} disabled={previewLoading || updatingIncome}>
                    Close
                  </SecondaryButton>
                  <SecondaryButton type="button" onClick={handleGeneratePreview} disabled={previewLoading || updatingIncome}>
                    {previewLoading ? 'Generating Preview...' : 'Preview Import'}
                  </SecondaryButton>
                  <PrimaryButton type="button" onClick={handleApplyUpdate} disabled={!canApplyUpdate || updatingIncome || previewLoading}>
                    {updatingIncome ? 'Updating Income...' : 'Update Income'}
                  </PrimaryButton>
                </FooterActions>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        <PaidReceiptsImportModal
          isOpen={paidReceiptModalOpen}
          onClose={() => setPaidReceiptModalOpen(false)}
          records={recoveryDueRecords}
          monthFilter={selectedRecoveryMonth}
          onImported={handlePaidReceiptImportCompleted}
        />

        {assignCounselorModalOpen && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitleWrap>
                  <ModalTitle>Assign Counselor</ModalTitle>
                  <ModalSubtitle>
                    Paste receipt HTML and enter counselor name. Matching receipt IDs will be updated.
                  </ModalSubtitle>
                </ModalTitleWrap>
                <CloseButton type="button" onClick={closeAssignCounselorModal} aria-label="Close counselor modal">
                  ×
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                <div>
                  <FieldLabel htmlFor="branch-new-income-counselor-name">Counselor Name</FieldLabel>
                  <Select
                    inputId="branch-new-income-counselor-name"
                    isClearable
                    isLoading={counselorOptionsLoading}
                    options={counselorStaffOptions}
                    value={assignCounselorOption}
                    placeholder={counselorOptionsLoading ? 'Loading staff...' : 'Select counselor'}
                    onChange={(value) => {
                      setAssignCounselorOption(value || null);
                      setAssignCounselorPreviewError('');
                    }}
                    formatOptionLabel={(option) => (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{option.label}</span>
                        {option.meta ? (
                          <span style={{ fontSize: 11, color: '#64748b' }}>{option.meta}</span>
                        ) : null}
                      </div>
                    )}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 44,
                        borderRadius: 12,
                        borderColor: state.isFocused ? '#10b981' : '#cbd5e1',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(16,185,129,0.14)' : 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? '#f0fdf4' : '#fff',
                        color: '#0f172a',
                      }),
                    }}
                  />
                  {assignCounselorOption?.data ? (
                    <div style={{ marginTop: 8, border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: 10, padding: '8px 10px', fontSize: 12, color: '#1e3a8a' }}>
                      Staff ID: {assignCounselorOption.data.staffId}
                      {assignCounselorOption.data.positionName ? ` • ${assignCounselorOption.data.positionName}` : ''}
                      {assignCounselorOption.data.employeeNumber ? ` • EMP ${assignCounselorOption.data.employeeNumber}` : ''}
                    </div>
                  ) : null}
                </div>

                <div>
                  <FieldLabel htmlFor="branch-new-income-counselor-html">HTML Table</FieldLabel>
                  {assignCounselorInputCollapsed && assignCounselorPreview ? (
                    <div style={{ border: '1px solid #dbeafe', background: '#f8fbff', borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <MetaText style={{ margin: 0, color: '#1e3a8a', fontWeight: 600 }}>
                        HTML table is collapsed to maximize preview space ({assignCounselorInput.length.toLocaleString()} chars)
                      </MetaText>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <SecondaryButton
                          type="button"
                          onClick={() => setAssignCounselorInputCollapsed(false)}
                          style={{ padding: '8px 12px', borderRadius: 10, fontSize: 12 }}
                        >
                          Edit HTML
                        </SecondaryButton>
                        <SecondaryButton
                          type="button"
                          onClick={() => {
                            setAssignCounselorInput('');
                            setAssignCounselorPreview(null);
                            setAssignCounselorPreviewError('');
                            setAssignCounselorInputCollapsed(false);
                          }}
                          style={{ padding: '8px 12px', borderRadius: 10, fontSize: 12 }}
                        >
                          Clear
                        </SecondaryButton>
                      </div>
                    </div>
                  ) : (
                    <TextArea
                      id="branch-new-income-counselor-html"
                      value={assignCounselorInput}
                      onChange={(event) => {
                        setAssignCounselorInput(event.target.value);
                        setAssignCounselorPreviewError('');
                      }}
                      placeholder="Paste the full HTML table here..."
                    />
                  )}
                </div>

                {assignCounselorPreviewError && <ErrorBanner style={{ margin: 0 }}>{assignCounselorPreviewError}</ErrorBanner>}

                {assignCounselorPreview && (
                  <PreviewCard>
                    <PreviewHeader>
                      <div>
                        <CardTitle>Counselor Assign Preview</CardTitle>
                        <CardSubtitle>
                          Preview by receipt ID before applying counselor assignment.
                        </CardSubtitle>
                      </div>
                      <StatusBadge $bg="#eff6ff" $border="#bfdbfe" $color="#1d4ed8">
                        {assignCounselorSummary.total} Parsed Rows
                      </StatusBadge>
                    </PreviewHeader>

                    <AssignSummaryRow>
                      <AssignSummaryChip $bg="#eff6ff" $border="#bfdbfe" $color="#1d4ed8">
                        Total Rows <AssignSummaryValue>{assignCounselorSummary.total}</AssignSummaryValue>
                      </AssignSummaryChip>
                      <AssignSummaryChip $bg="#f8fbff" $border="#93c5fd" $color="#1d4ed8">
                        Will Update <AssignSummaryValue>{assignCounselorSummary.update}</AssignSummaryValue>
                      </AssignSummaryChip>
                      <AssignSummaryChip $bg="#ecfdf5" $border="#86efac" $color="#166534">
                        Unchanged <AssignSummaryValue>{assignCounselorSummary.unchanged}</AssignSummaryValue>
                      </AssignSummaryChip>
                      <AssignSummaryChip $bg="#fff7ed" $border="#fdba74" $color="#c2410c">
                        Not Found <AssignSummaryValue>{assignCounselorSummary.notFound}</AssignSummaryValue>
                      </AssignSummaryChip>
                      <AssignSummaryChip $bg="#fef2f2" $border="#fca5a5" $color="#b91c1c">
                        Needs Attention <AssignSummaryValue>{assignCounselorSummary.invalid}</AssignSummaryValue>
                      </AssignSummaryChip>
                    </AssignSummaryRow>

                    <AssignPreviewTableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <Th>#</Th>
                            <Th>Receipt ID</Th>
                            <Th>Action</Th>
                            <Th>Current Counselor</Th>
                            <Th>New Counselor</Th>
                            <Th>Down Payment Completed Date</Th>
                            <Th $alignRight>Earnable Points</Th>
                            <Th $alignRight>Earned KPI Points</Th>
                            <Th>Validation</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignCounselorPreview.previewRows.map((row) => {
                            const tone = AssignTone[row.status] || AssignTone.update;
                            const hasErrors = row.errors?.length > 0;
                            return (
                              <StatusRow key={`${row.receiptId}-${row.rowIndex}`} $status={row.status === 'not_found' ? 'invalid' : row.status}>
                                <Td><ReceiptMeta>#{row.rowIndex}</ReceiptMeta></Td>
                                <Td><ReceiptId>{row.receiptId || '—'}</ReceiptId></Td>
                                <Td>
                                  <StatusBadge $bg={tone.bg} $border={tone.border} $color={tone.color}>
                                    {row.statusLabel}
                                  </StatusBadge>
                                </Td>
                                <Td>{row.existingCounselor || '—'}</Td>
                                <Td>{row.incomingCounselor || '—'}</Td>
                                <Td>{row.downPaymentCompletedDate || '—'}</Td>
                                <Td $alignRight>{row.earnablePoints === null || row.earnablePoints === undefined ? '—' : row.earnablePoints}</Td>
                                <Td $alignRight>{row.earnedKpiPoints === null || row.earnedKpiPoints === undefined ? '—' : row.earnedKpiPoints}</Td>
                                <Td>
                                  {hasErrors ? (
                                    <DiffList>
                                      {row.errors.map((message) => (
                                        <DiffItem key={message} style={{ color: '#b91c1c' }}>{message}</DiffItem>
                                      ))}
                                    </DiffList>
                                  ) : row.changedFields?.length > 0 ? (
                                    <DiffList>
                                      {row.changedFields.map((field) => (
                                        <DiffItem key={`${row.receiptId}-${field.field}`}>
                                          {field.label}: {field.existing ?? '—'} -> {field.incoming ?? '—'}
                                        </DiffItem>
                                      ))}
                                    </DiffList>
                                  ) : row.status === 'update' ? (
                                    <DiffItem style={{ color: '#1d4ed8' }}>Counselor will be updated for this receipt.</DiffItem>
                                  ) : row.status === 'unchanged' ? (
                                    <DiffItem style={{ color: '#166534' }}>Already assigned to this counselor.</DiffItem>
                                  ) : (
                                    <DiffItem>—</DiffItem>
                                  )}
                                </Td>
                              </StatusRow>
                            );
                          })}
                        </tbody>
                      </Table>
                    </AssignPreviewTableWrap>
                  </PreviewCard>
                )}
              </ModalBody>

              <ModalFooter>
                <MetaText>
                  {assignCounselorPreview
                    ? canApplyCounselorAssign
                      ? `Ready to assign counselor for ${assignCounselorSummary.update} receipt${assignCounselorSummary.update === 1 ? '' : 's'}.`
                      : assignCounselorSummary.invalid > 0
                        ? 'Resolve invalid receipt rows before assigning counselor.'
                        : 'No counselor changes were found for the current preview.'
                    : assignCounselorOption
                      ? 'Generate preview to review receipt matching before updating counselor.'
                      : 'Select counselor first, then generate preview.'}
                </MetaText>
                <FooterActions>
                  <SecondaryButton type="button" onClick={closeAssignCounselorModal} disabled={assignCounselorPreviewLoading || assignCounselorUpdating}>
                    Close
                  </SecondaryButton>
                  <SecondaryButton type="button" onClick={handlePreviewCounselorAssign} disabled={!assignCounselorOption || assignCounselorPreviewLoading || assignCounselorUpdating}>
                    {assignCounselorPreviewLoading ? 'Generating Preview...' : 'Preview Assign'}
                  </SecondaryButton>
                  <PrimaryButton type="button" onClick={handleApplyCounselorAssign} disabled={!canApplyCounselorAssign || assignCounselorUpdating || assignCounselorPreviewLoading}>
                    {assignCounselorUpdating ? 'Assigning Counselor...' : 'Assign Counselor'}
                  </PrimaryButton>
                </FooterActions>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}
      </PageSection>

      {/* ── Counselor Detail Drawer ──────────────────────────────── */}
      <DrawerOverlay $open={!!drawerCounselor} onClick={() => setDrawerCounselor(null)} />
      <DrawerPanel $open={!!drawerCounselor} role="dialog" aria-modal="true" aria-label="Counselor registrations">
        {drawerCounselor && (() => {
          const achieved = Number(drawerCounselor.kpiAchieved) || 0;
          const locked = drawerCounselor.kpiLocked !== null && drawerCounselor.kpiLocked !== undefined ? Number(drawerCounselor.kpiLocked) : null;
          const achievedPct = locked > 0 ? (achieved / locked) * 100 : null;
          const totalAmt = drawerRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
          return (
            <>
              <DrawerHeader>
                <DrawerTitleGroup>
                  <DrawerEyebrow>{isAllRegistrationsDrawer ? 'All Registrations' : 'Counselor Registrations'}</DrawerEyebrow>
                  <DrawerTitle title={selectedCounselorName}>{selectedCounselorName}</DrawerTitle>
                  <DrawerSubtitle>{getMonthLabel(month)} {year}</DrawerSubtitle>
                </DrawerTitleGroup>
                <DrawerCloseBtn onClick={() => setDrawerCounselor(null)} aria-label="Close">✕</DrawerCloseBtn>
              </DrawerHeader>

              <DrawerStatsBar>
                <DrawerStat>
                  <DrawerStatLabel>Registrations</DrawerStatLabel>
                  <DrawerStatValue>{Number(drawerCounselor.registrations) || 0}</DrawerStatValue>
                </DrawerStat>
                <DrawerStat>
                  <DrawerStatLabel>Total Sales</DrawerStatLabel>
                  <DrawerStatValue>{formatAmount(drawerCounselor.salesAmount)}</DrawerStatValue>
                </DrawerStat>
                {!isAllRegistrationsDrawer && locked !== null && (
                  <DrawerStat>
                    <DrawerStatLabel>KPI</DrawerStatLabel>
                    <DrawerStatValue>
                      {achieved.toFixed(1)}{achievedPct !== null ? ` / ${locked.toFixed(1)}` : ''}
                      {achievedPct !== null && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: achievedPct >= 100 ? '#16a34a' : '#64748b', marginLeft: 4 }}>
                          ({achievedPct.toFixed(0)}%)
                        </span>
                      )}
                    </DrawerStatValue>
                  </DrawerStat>
                )}
              </DrawerStatsBar>

              <DrawerBody>
                {drawerRows.length === 0 ? (
                  <EmptyState>
                    {isAllRegistrationsDrawer
                      ? 'No registration records found for the selected period.'
                      : 'No registration records found for this counselor in the selected period.'}
                  </EmptyState>
                ) : (
                  <DrawerTable>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Receipt ID</th>
                        <th>Transaction Date</th>
                        <th>Reg ID</th>
                        <th>Student</th>
                        <th>Course</th>
                        <th>Payment Type</th>
                        <th>Amount</th>
                        <th>Down Payment Date</th>
                        <th>Earnable Pts</th>
                        <th>Earned KPI Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawerRows.map((r, idx) => (
                        <tr key={r.id}>
                          <td style={{ color: '#94a3b8', fontWeight: 600 }}>{idx + 1}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#475569' }}>{r.receiptId || '—'}</td>
                          <td>{formatDateTime(r.transactionDate)}</td>
                          <td style={{ fontWeight: 700 }}>{r.registrationId}</td>
                          <td>{r.student}</td>
                          <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.course}</td>
                          <td>
                            <PaymentBadge style={{ fontSize: 11, padding: '3px 8px' }}>{r.paymentType}</PaymentBadge>
                          </td>
                          <td><AmountText style={{ fontSize: 13 }}>{formatAmount(r.amount)}</AmountText></td>
                          <td>{formatDateOnly(r.downPaymentCompletedDate)}</td>
                          <td style={{ textAlign: 'right' }}>{formatDecimal(r.earnablePoints)}</td>
                          <td style={{ textAlign: 'right' }}>{formatDecimal(r.earnedKpiPoints)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DrawerTable>
                )}
              </DrawerBody>

              <DrawerTableFooter>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <SecondaryButton
                    type="button"
                    onClick={handleExportCounselorExcel}
                    disabled={drawerRows.length === 0}
                    style={{ padding: '7px 12px', borderRadius: 10, fontSize: 12 }}
                  >
                    Download Excel
                  </SecondaryButton>
                  <span>{drawerRows.length} record{drawerRows.length === 1 ? '' : 's'}</span>
                </div>
                <span>Total: <strong style={{ color: '#166534' }}>{formatAmount(totalAmt)}</strong></span>
              </DrawerTableFooter>
            </>
          );
        })()}
      </DrawerPanel>
    </AdminSalesLayout>
  );
}